// =====================================================
// 📁 components/POS/Fields/POSSearchInput.tsx
// =====================================================

import { Search } from 'lucide-react';

import { useEffect, useState } from 'react';

import useDebounced from '../../../hooks/useDebounced';

interface Props {
  value?: string;

  onChange?: (value: string) => void;

  onDebouncedChange?: (value: string) => void;

  placeholder?: string;

  disabled?: boolean;

  debounce?: number;

  onFocus?: () => void;

  onBlur?: () => void;

  name?: string;
}

const POSSearchInput: React.FC<Props> = ({
  value = '',

  onChange,

  onDebouncedChange,

  placeholder = 'Buscar...',

  disabled = false,

  debounce = 400,

  onFocus,

  onBlur,

  name,
}) => {
  // =====================================================
  // 🔹 State
  // =====================================================

  const [internalValue, setInternalValue] = useState(value);

  // =====================================================
  // 🔹 Debounced
  // =====================================================

  const debouncedValue = useDebounced(internalValue, debounce);

  // =====================================================
  // 🔹 Sync External
  // =====================================================

  useEffect(() => {
    setInternalValue(value);
  }, [value]);

  // =====================================================
  // 🔹 Debounced Callback
  // =====================================================

  useEffect(() => {
    if (onDebouncedChange) {
      onDebouncedChange(debouncedValue);
    }
  }, [debouncedValue]);

  // =====================================================
  // 🎨 Render
  // =====================================================

  return (
    <div
      className="
        flex
        h-[44px]
        items-center
        gap-3

        rounded-xl

        border
        border-stroke

        bg-white

        px-4

        transition-all

        focus-within:border-primary

        dark:border-strokedark
        dark:bg-boxdark
      "
    >
      {/* ===================================================== */}
      {/* 🔹 Icon */}
      {/* ===================================================== */}

      <Search
        className="
          h-4
          w-4
          shrink-0

          text-slate-400
        "
      />

      {/* ===================================================== */}
      {/* 🔹 Input */}
      {/* ===================================================== */}

      <input
        type="text"
        name={name}
        value={internalValue}
        disabled={disabled}
        placeholder={placeholder}
        onFocus={onFocus}
        onBlur={onBlur}
        onChange={(e) => {
          const value = e.target.value;

          setInternalValue(value);

          onChange?.(value);
        }}
        className="
          w-full

          bg-transparent

          text-sm
          text-slate-700

          outline-none

          placeholder:text-slate-400

          dark:text-white
        "
      />
    </div>
  );
};

export default POSSearchInput;
